import { displayText, clientUserText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import {
  createTrial,
  getTrial,
  updateTrial,
  getCsrfToken,
  getCase,
} from '../../../../services/api';
import api from '../../../../services/api';
import { validateField, validateForm } from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import ClientConfig from '../../../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';

//  shorthand for this session type
const SC = ClientConfig.sessions.types.trial;

const AddTrial = ({ isEdit = false }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const params = useParams();
  const navigate = useNavigate();

  const caseIdStr = params.caseId || params.id;
  const sessionIdStr = params.sessionId;

  const caseId = caseIdStr ? parseInt(caseIdStr, 10) : null;
  const sessionId = sessionIdStr ? parseInt(sessionIdStr, 10) : null;
  const [touched, setTouched] = useState({});

  const generatePasscode = () => {
    return Math.floor(10000 + Math.random() * 90000).toString();
  };

  const [formData, setFormData] = useState({
  name: '',
  introducing_party: [],
  date: '',
  details: '',
  location: '',
  session_passcode: generatePasscode(),

  schedule_video: false,
  zoom_tech: '',
  registrants: '',
  meeting_duration: '60',
  meeting_time: '',
  meeting_timezone: '',

  // Zoom meeting fields
  zoom_meeting_id: '',
  zoom_join_url: '',
  zoom_start_url: '',
  zoom_password: '',
  zoom_created_at: '',
});

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Early validation
  useEffect(() => {
    if (!caseId || isNaN(caseId)) {
      toast.error(displayText('Invalid case ID'));
      navigate(`/${params.username}/dashboard/${URL_SEGMENTS.cases}`);
      return;
    }

    if (isEdit && (!sessionId || isNaN(sessionId))) {
      toast.error(displayText('Invalid session ID'));
      navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      return;
    }
  }, [caseId, sessionId, isEdit, navigate, params.username]);

  // Initialize CSRF and fetch combined users
  useEffect(() => {
    const initializeCsrf = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        console.error('Error initializing CSRF token:', error);
        toast.error('Failed to initialize security token.');
      }
    };
    initializeCsrf();

    const fetchCombinedUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await api.get('/client-users/combined-for-introducing');
        if (response.data.success) {
          setCombinedUsers(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching combined users:', error);
        toast.error('Failed to load users');
        setCombinedUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchCombinedUsers();
  }, []);

  // Fetch case details
  useEffect(() => {
    if (!caseId) return;

    const fetchCaseDetails = async () => {
      try {
        const caseResponse = await getCase(caseId);
        setCaseData(caseResponse.data.data);
      } catch (caseError) {
        console.error('Error fetching case details:', caseError);
        toast.error(displayText('Failed to fetch case details.'));
        navigate(`/${params.username}/dashboard/${URL_SEGMENTS.cases}`);
      }
    };
    fetchCaseDetails();
  }, [caseId, navigate, params.username]);

  // Fetch trial data when in edit mode
  useEffect(() => {
    if (!isEdit || !sessionId) return;

    const fetchTrial = async () => {
      try {
        setIsLoading(true);
        const response = await getTrial(sessionId);
const trial = response.data?.data || response.data;

if (!trial) {
  throw new Error('No trial data received');
}

if (Number(trial.case_id) !== Number(caseId)) {
  throw new Error('This trial does not belong to the current case');
}

const formattedDate = trial.date ? trial.date.split('T')[0] : '';

const introducingParties = trial.introducing_party
  ? trial.introducing_party.split(',').map((p) => p.trim()).filter(Boolean)
  : [];

setFormData({
  name: trial.name || '',
  introducing_party: introducingParties,
  date: formattedDate,
  details: trial.details || '',
  location: trial.location || '',
  session_passcode: trial.session_passcode || generatePasscode(),

  schedule_video: !!trial.schedule_video,
  zoom_tech: trial.zoom_tech || (trial.schedule_video ? 'zoom' : ''),
  registrants: trial.registrants || '',
  meeting_duration: trial.meeting_duration || '60',
  meeting_time: trial.meeting_time || '',
  meeting_timezone: trial.meeting_timezone || '',

  zoom_meeting_id: trial.zoom_meeting_id || '',
  zoom_join_url: trial.zoom_join_url || '',
  zoom_start_url: trial.zoom_start_url || '',
  zoom_password: trial.zoom_password || trial.session_passcode || '',
  zoom_created_at: trial.zoom_created_at || '',
});

        console.log('Trial data loaded successfully:', trial);
      } catch (error) {
        console.error('Failed to fetch trial:', error);
        toast.error(displayText('Failed to load trial data: ') + (error.message || 'Unknown error'));
        navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTrial();
  }, [isEdit, sessionId, caseId, navigate, params.username]);

  const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target || {};

  if (name === 'schedule_video') {
    const isEnabled = checked;

    setFormData((prev) => ({
      ...prev,
      schedule_video: isEnabled,
      zoom_tech: isEnabled ? 'zoom' : '',
      registrants: isEnabled ? prev.registrants : '',
      meeting_duration: isEnabled ? (prev.meeting_duration || '60') : '',
      meeting_time: isEnabled ? prev.meeting_time : '',
      meeting_timezone: isEnabled ? prev.meeting_timezone : '',

      zoom_meeting_id: isEnabled ? prev.zoom_meeting_id : '',
      zoom_join_url: isEnabled ? prev.zoom_join_url : '',
      zoom_start_url: isEnabled ? prev.zoom_start_url : '',
      zoom_password: isEnabled ? (prev.session_passcode || '') : '',
      zoom_created_at: isEnabled ? prev.zoom_created_at : '',
    }));

    setErrors((prev) => ({
      ...prev,
      zoom_tech: '',
      registrants: '',
      meeting_duration: '',
      meeting_time: '',
      meeting_timezone: '',
    }));

    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));

  if (errors[name]) {
    setErrors((prev) => ({ ...prev, [name]: '' }));
  }
};

  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'trial');
  };

  const handleIntroducingPartySelect = (e) => {
    const selectedValue = e.target.value;
    setTouched((prev) => ({ ...prev, introducing_party: true }));
    if (selectedValue && !formData.introducing_party.includes(selectedValue)) {
      setFormData((prev) => ({
        ...prev,
        introducing_party: [...prev.introducing_party, selectedValue],
      }));
      e.target.value = '';
    }
  };

  const removeIntroducingParty = (partyToRemove) => {
    setFormData((prev) => ({
      ...prev,
      introducing_party: prev.introducing_party.filter((p) => p !== partyToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
 

    setTouched((prev) => ({ ...prev, introducing_party: true }));
    setIsSubmitting(true);
    setIsLoading(true);

    if (formData.introducing_party.length === 0) {
      toast.error('Please select at least one introducing party');
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

  try {
    const cleanedIntroducingParty = formData.introducing_party
      .map((name) => name.replace(/^"|"$/g, '').trim())
      .filter(Boolean);

    const formDataForValidation = {
      ...formData,
      introducing_party: cleanedIntroducingParty.join(', '),
    };

    const isValid = await validateForm(formDataForValidation, setErrors, 'trial');

      if (!isValid) {
        toast.error('Please fill all required fields correctly.');
        return;
      }

    const payload = {
      case_id: Number(caseId),
      name: formData.name?.trim() || '',
      introducing_party: cleanedIntroducingParty.join(', '),
      date: formData.date || null,
      details: formData.details?.trim() || null,
      location: formData.location?.trim() || '',
      session_passcode: formData.session_passcode || '',
      schedule_video: !!formData.schedule_video,
      zoom_tech: formData.schedule_video ? 'zoom' : null,
      registrants: formData.schedule_video ? (formData.registrants?.trim() || null) : null,
      meeting_duration: formData.schedule_video ? (formData.meeting_duration || null) : null,
      meeting_time: formData.schedule_video ? formData.meeting_time : null,
      meeting_timezone: formData.schedule_video ? formData.meeting_timezone : null,
    };

      console.log('FINAL PAYLOAD being sent to backend:', payload);

    let response;
    if (isEdit) {
      response = await updateTrial(sessionId, payload);
    } else {
      response = await createTrial(payload);
    }

    if ([200, 201, 204].includes(response.status)) {
      toast.success(`${displayText("Trial session ")}${isEdit ? 'updated' : 'added'}${displayText(" successfully")}`);
      navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
    } else {
      throw new Error(`Unexpected status: ${response.status}`);
    }
  } catch (error) {
    console.error('Trial submission failed:', error);

      //  SC.label
      let errorMessage = `Failed to save ${SC.label} session`;

      if (error.response?.data) {
        console.log('Backend full response:', error.response.data);

      if (error.response.data.errors) {
        const firstField = Object.keys(error.response.data.errors)[0];
        const messages = error.response.data.errors[firstField];
        errorMessage = `${firstField}: ${messages.join(' ')}`;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
  };

  return (
    <>
      <div style={{ padding: '0rem 1.0rem 1.0rem 0rem', margin: 0 }}>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0" style={{ fontSize: '10px', padding: 0, background: 'transparent' }}>
            <li className="breadcrumb-item">
              <Link to={`/${params.username}/dashboard/${URL_SEGMENTS.cases}`} style={{ color: theme.colors.primary, textDecoration: 'none', fontWeight: 'bold' }}>
                {/*  ClientConfig */}
                {ClientConfig.cases.managementLabel}
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`} style={{ color: theme.colors.textPrimary, textDecoration: 'none', fontWeight: 'bold' }}>
                {caseData?.case_name || 'Loading...'}
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}>
              {/*  SC.label */}
              {isEdit ? 'Edit' : 'Add'}: {SC.label}
            </li>
          </ol>
        </nav>
      </div>

      <div className="container-fluid py-4" style={{ backgroundColor: theme.colors.bgPrimary, color: theme.colors.textPrimary, fontFamily: theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif', fontSize: 'clamp(10px, 2.5vw, 11px)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', border: `1px solid ${theme.colors.borderColor || '#ddd'}`, margin: '0 1.0rem 1.0rem 0' }}>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" toastStyle={{ backgroundColor: theme.colors.bgPrimary, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.borderColor}`, fontSize: '10px', fontWeight: '500' }} />

        <div style={{ borderRadius: '10px', border: `1px solid ${theme.colors.borderColor}`, overflow: 'hidden', backgroundColor: theme.colors.bgPrimary }}>
          <div style={{ background: theme.gradients.primary, color: theme.colors.textWhite, padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: window.innerWidth < 600 ? 'column' : 'row', gap: '12px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {/*  SC.label */}
              {isEdit ? 'Edit' : 'Add'}: {SC.label}
            </h5>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {/*  SC.relatedToLabel */}
              {SC.relatedToLabel} : {caseData?.case_name || 'Loading...'}
            </h5>
          </div>

          <div style={{ padding: '16px' }}>
            <form id="addTrialForm" onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row', gap: '24px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Name */}
                  <div>
                    <label htmlFor="name" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      {/*  SC.fieldLabel */}
                      {SC.fieldLabel}*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Assist name"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.name ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit' }}
                    />
                    {errors.name && <div className="invalid-feedback" style={{ color: theme.colors.danger, display: 'block', fontSize: '9px', fontWeight: '500', marginTop: '2px' }}>{errors.name}</div>}
                  </div>

                  {/* Introducing Party */}
                  <div>
                    <label htmlFor="introducingParty" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>{clientUserText(" Introducing Party* (Client Admins & Users) ")}</label>
                    <select
                      id="introducingParty"
                      onChange={handleIntroducingPartySelect}
                      disabled={isSubmitting || loadingUsers}
                      className={`form-control ${errors.introducing_party ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.introducing_party ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit' }}
                    >
                      <option value="">{loadingUsers ? 'Loading...' : 'Select to add...'}</option>
                      {combinedUsers.map((user) => (
                        <option key={`${user.type}-${user.id}`} value={user.display_name}>
                          {user.display_name}
                        </option>
                      ))}
                    </select>

                    {formData.introducing_party.length > 0 && touched.introducing_party && (
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {formData.introducing_party.map((party, index) => (
                          <div key={index} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: theme.colors.primary, color: theme.colors.textWhite, padding: '4px 8px', borderRadius: '12px', fontSize: '9px', fontWeight: '500' }}>
                            <span style={{ marginRight: '6px' }}>{party}</span>
                            <button type="button" onClick={() => removeIntroducingParty(party)} style={{ background: 'transparent', border: 'none', color: theme.colors.textWhite, cursor: 'pointer', fontSize: '12px', padding: '0', lineHeight: '1' }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {formData.introducing_party.length === 0 && (
                      <div style={{ fontSize: '9px', color: theme.colors.danger, marginTop: '4px' }}>
                        Please select at least one introducing party
                      </div>
                    )}
                    {errors.introducing_party && <div className="invalid-feedback" style={{ color: theme.colors.danger, display: 'block', fontSize: '9px', fontWeight: '500', marginTop: '2px' }}>{errors.introducing_party}</div>}
                  </div>

                  {/* Date */}
                  <div>
                    <label htmlFor="date" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.date ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit' }}
                    />
                    {errors.date && <div className="invalid-feedback" style={{ color: theme.colors.danger, display: 'block', fontSize: '9px', fontWeight: '500', marginTop: '2px' }}>{errors.date}</div>}
                  </div>

                  {/* Details */}
                  <div>
                    <label htmlFor="details" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.details ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', resize: 'vertical', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit' }}
                    ></textarea>
                    {errors.details && <div className="invalid-feedback" style={{ color: theme.colors.danger, display: 'block', fontSize: '9px', fontWeight: '500', marginTop: '2px' }}>{errors.details}</div>}
                  </div>

                  {/* Location */}
                  <div>
                    <label htmlFor="location" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      {/*  SC.locationLabel */}
                      {SC.locationLabel}
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.location ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit' }}
                    />
                    {errors.location && <div className="invalid-feedback" style={{ color: theme.colors.danger, display: 'block', fontSize: '9px', fontWeight: '500', marginTop: '2px' }}>{errors.location}</div>}
                  </div>

                  {/* Session Passcode */}
                  <div>
                    <label htmlFor="sessionPasscode" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>{displayText(" Session Passcode* (Auto-Generated) ")}</label>
                    <input
                      type="text"
                      id="sessionPasscode"
                      name="session_passcode"
                      value={formData.session_passcode}
                      readOnly
                      disabled={isSubmitting}
                      style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', opacity: isSubmitting ? 0.6 : 1, fontFamily: 'inherit', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>

                {/* Divider */}
                <div style={{ width: window.innerWidth < 600 ? '100%' : '1px', height: window.innerWidth < 600 ? '1px' : 'auto', backgroundColor: 'transparent', backgroundImage: window.innerWidth < 600 ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)` : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`, margin: window.innerWidth < 600 ? '12px 0' : '0 16px', minHeight: window.innerWidth >= 600 ? '400px' : '1px' }} />

                {/* Right Column */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Video Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label htmlFor="scheduleVideo" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', margin: 0, opacity: isSubmitting ? 0.6 : 1 }}>
                      Schedule a Video Conference?
                    </label>
                    <div
                      style={{ position: 'relative', width: '50px', height: '24px', borderRadius: '12px', backgroundColor: formData.schedule_video ? theme.colors.success : '#ccc', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'background-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1 }}
                      onClick={() => {
  if (!isSubmitting) {
    handleInputChange({
      target: {
        name: 'schedule_video',
        checked: !formData.schedule_video,
      },
    });
  }
}}
                    >
                      <div style={{ position: 'absolute', top: '2px', left: formData.schedule_video ? '28px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: theme.colors.textWhite, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'left 0.3s ease' }} />
                      <input type="checkbox" id="scheduleVideo" name="schedule_video" checked={formData.schedule_video} onChange={handleInputChange} disabled={isSubmitting} style={{ display: 'none' }} />
                    </div>
                  </div>

                  {formData.schedule_video && (
  <>
    <div>
      <label htmlFor="zoomMeetingId" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Zoom Meeting ID
      </label>
      <input
        type="text"
        id="zoomMeetingId"
        name="zoom_meeting_id"
        value={formData.zoom_meeting_id}
        readOnly
        disabled
        className="form-control"
        placeholder="Will be generated after Zoom meeting is created"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333' }}
      />
    </div>

    <div>
      <label htmlFor="zoomJoinUrl" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Zoom Join URL
      </label>
      <input
        type="text"
        id="zoomJoinUrl"
        name="zoom_join_url"
        value={formData.zoom_join_url}
        readOnly
        disabled
        className="form-control"
        placeholder="Will be generated after Zoom meeting is created"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333' }}
      />
    </div>

    <div>
      <label htmlFor="zoomStartUrl" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Zoom Start URL
      </label>
      <input
        type="text"
        id="zoomStartUrl"
        name="zoom_start_url"
        value={formData.zoom_start_url}
        readOnly
        disabled
        className="form-control"
        placeholder="Will be generated after Zoom meeting is created"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333' }}
      />
    </div>

    <div>
      <label htmlFor="zoomPassword" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Zoom Password
      </label>
      <input
        type="text"
        id="zoomPassword"
        name="zoom_password"
        value={formData.zoom_password}
        readOnly
        disabled
        className="form-control"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333' }}
      />
    </div>

    <div>
      <label htmlFor="zoomCreatedAt" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Zoom Created At
      </label>
      <input
        type="text"
        id="zoomCreatedAt"
        name="zoom_created_at"
        value={formData.zoom_created_at}
        readOnly
        disabled
        className="form-control"
        placeholder="Will be filled after Zoom meeting is created"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333' }}
      />
    </div>

    <div>
      <label htmlFor="registrants" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Registrants
      </label>
      <input
        type="text"
        id="registrants"
        name="registrants"
        value={formData.registrants}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder="Registrants"
        disabled={isSubmitting}
        className={`form-control ${errors.registrants ? 'is-invalid' : ''}`}
        style={{ width: '100%', border: `1px solid ${errors.registrants ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
      />
    </div>

    <div>
      <label htmlFor="meetingDuration" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Meeting Duration (mins)
      </label>
      <input
        type="number"
        id="meetingDuration"
        name="meeting_duration"
        value={formData.meeting_duration}
        onChange={handleInputChange}
        disabled={isSubmitting}
        className="form-control"
        min="15"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
      />
    </div>

    <div>
      <label htmlFor="meetingTime" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Meeting Time
      </label>
      <input
        type="time"
        id="meetingTime"
        name="meeting_time"
        value={formData.meeting_time}
        onChange={handleInputChange}
        disabled={isSubmitting}
        className="form-control"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
      />
    </div>

    <div>
      <label htmlFor="meetingTimezone" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
        Meeting Timezone
      </label>
      <select
        id="meetingTimezone"
        name="meeting_timezone"
        value={formData.meeting_timezone}
        onChange={handleInputChange}
        disabled={isSubmitting}
        className="form-control"
        style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
      >
        <option value="">Select timezone</option>
        <option value="Asia/Kolkata">Asia/Kolkata</option>
        <option value="UTC">UTC</option>
        <option value="America/New_York">America/New_York</option>
        <option value="America/Los_Angeles">America/Los_Angeles</option>
      </select>
    </div>
  </>
)}
                        
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row', justifyContent: 'space-between', alignItems: window.innerWidth < 600 ? 'stretch' : 'center', padding: '12px', backgroundColor: theme.colors.bgPrimary }}>
          <div style={{ display: 'flex', gap: '8px', flexDirection: window.innerWidth < 600 ? 'column' : 'row', width: window.innerWidth < 600 ? '100%' : 'auto', position: 'relative' }}>
            <button onClick={handleCancel} disabled={isSubmitting} style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, fontSize: '9px', fontWeight: '600', padding: '8px 12px', borderRadius: '8px', border: 'none', width: window.innerWidth < 600 ? '100%' : 'auto', minWidth: '120px', opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
            <button type="submit" form="addTrialForm" disabled={isLoading} style={{ background: theme.gradients.primary, color: theme.colors.textWhite, fontSize: '9px', fontWeight: '600', padding: '8px 12px', borderRadius: '8px', border: 'none', width: window.innerWidth < 600 ? '100%' : 'auto', minWidth: '120px', opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
            </button>
            {isLoading && (
              <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
                <div style={{ width: '40px', height: '40px', border: `4px solid ${theme.colors.primary}`, borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            )}
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTrial;